/**
 * Project Categories Taxonomy with Weighted Categorization
 * 
 * A comprehensive taxonomy system for planning applications using weighted hit words
 * to determine primary and secondary categories for each application.
 */
import categoryWeights from './categoryWeights.json';
import {
  applyBoostsAndPenalties,
  applyRefPrefixShortcuts,
  normalizeWithStemming,
  applyShortDescriptionBoost,
  PRECEDENCE
} from './scoringRules';

const normalizeIconPath = (iconPath) => {
  if (!iconPath || typeof iconPath !== 'string') return iconPath;
  let trimmed = iconPath.trim().replace(/\\/g, '/');
  if (
    trimmed.startsWith('/PlanningPageCategories/') &&
    !trimmed.startsWith('/PlanningPageCategories/thumbs/')
  ) {
    trimmed = trimmed.replace(
      '/PlanningPageCategories/',
      '/PlanningPageCategories/thumbs/'
    );
  }
  if (!trimmed.startsWith('/')) return trimmed;
  const segments = trimmed.split('/');
  return segments
    .map((segment, index) => (index === 0 ? segment : encodeURIComponent(segment)))
    .join('/');
};

const rawProjectCategories = [
  {
    "id": "new_build_major",
    "label": "New-Build & Major Development",
    "icon": "/PlanningPageCategories/New-Build & Major Development.png"
  },
  {
    "id": "change_of_use_conversions",
    "label": "Change of Use & Conversions",
    "icon": "/PlanningPageCategories/Change of Use & Conversions.png"
  },
  {
    "id": "short_term_lets",
    "label": "Short Term Lets",
    "icon": "/PlanningPageCategories/Change of Use & Conversions.png"
  },
  {
    "id": "extensions_enlargements",
    "label": "Extensions & Enlargements",
    "icon": "/PlanningPageCategories/Extensions & Enlargements.png"
  },
  {
    "id": "demolition",
    "label": "Demolition & Site Clearance",
    "icon": "/PlanningPageCategories/Demolition & Site Clearance.png"
  },
  {
    "id": "outbuildings_ancillary",
    "label": "Outbuildings & Ancillary Structures",
    "icon": "/PlanningPageCategories/Outbuildings & Ancillary Structures.png"
  },
  {
    "id": "windows_doors_glazing",
    "label": "Windows, Doors & Glazing",
    "icon": "/PlanningPageCategories/Windows, Doors & Glazing.png"
  },
  {
    "id": "roof_works_dormers",
    "label": "Roof Works & Dormers",
    "icon": "/PlanningPageCategories/Roof Works & Dormers.png"
  },
  {
    "id": "mechanical_services",
    "label": "Mechanical Plant, Flues & Ventilation",
    "icon": "/PlanningPageCategories/Mechanical Plant, Flues & Ventilation.png"
  },
  {
    "id": "renewables_energy",
    "label": "Renewable Energy & Sustainability",
    "icon": "/PlanningPageCategories/Renewable Energy & Sustainability.png"
  },
  {
    "id": "landscaping_boundaries",
    "label": "Landscaping, Gardens & Boundaries",
    "icon": "/PlanningPageCategories/Landscaping, Gardens & Boundaries.png"
  },
  {
    "id": "parking_access",
    "label": "Parking, Access & Highways",
    "icon": "/PlanningPageCategories/Parking, Access & Highways.png"
  },
  {
    "id": "external_lighting",
    "label": "External Lighting & Fixtures",
    "icon": "/PlanningPageCategories/External Lighting & Fixtures.png"
  },
  {
    "id": "trees_ecology",
    "label": "Trees, Ecology & Green Space",
    "icon": "/PlanningPageCategories/Trees, Ecology & Green Space.png"
  },
  {
    "id": "legal_admin",
    "label": "Legal / Administrative",
    "icon": "/PlanningPageCategories/Legal & Administrative.png"
  },
  {
    "id": "interiors_accessibility",
    "label": "Internal Works & Accessibility",
    "icon": "/PlanningPageCategories/Internal Works & Accessibility.png"
  },
  {
    "id": "shopfront_signage",
    "label": "Shopfront & Signage",
    "icon": "/PlanningPageCategories/Shopfront & Signage.png"
  },
  {
    "id": "public_realm_community",
    "label": "Public Realm & Community Projects",
    "icon": "/PlanningPageCategories/Public Realm & Community Projects.png"
  },
  {
    "id": "uncategorized",
    "label": "Uncategorised",
    "icon": "/PlanningPageCategories/Legal & Administrative.png"
  },
  {
    "id": "telecommunications",
    "label": "Telecommunications",
    "icon": "/PlanningPageCategories/Telecommunications.png"
  }
];

export const projectCategories = rawProjectCategories.map((category) => ({
  ...category,
  icon: normalizeIconPath(category.icon),
}));

/**
 * Categorizes an application by analyzing its description and reference number,
 * implementing the two-tier weighting system with boosts and penalties.
 * 
 * @param {string} description - The application description to categorize
 * @param {string} referenceNo - The application reference number
 * @returns {Object} An object containing the primary category ID, an array of secondary category IDs, and a lowConfidence flag
 */
export const categorizeApplication = (description, referenceNo = "") => {
  const cacheKey = `${referenceNo}|${description}`;
  if (categorizeCache.has(cacheKey)) return categorizeCache.get(cacheKey);
  if (!description) return { primaryCategory: null, secondaryCategories: [], lowConfidence: false };
  
  // Normalize and stem the description for better matching
  const normalizedDescription = normalizeWithStemming(description);
  
  // Step 1: Initial keyword scoring from weight table
  const scores = initialKeywordScoring(normalizedDescription);
  
  // Step 2: Apply boosts and penalties based on co-occurring words
  applyBoostsAndPenalties(normalizedDescription, scores);
  
  // Step 3: Apply reference prefix shortcuts
  applyRefPrefixShortcuts(referenceNo, scores);
  
  // Step 4: Apply special case rules
  applySpecialCaseRules(normalizedDescription, scores);
  
  // Step 5: Apply short description boost if needed
  applyShortDescriptionBoost(normalizedDescription, scores, 2);
  
  // Step 6: Sort categories by score and apply elastic threshold
  const sortedEntries = Object.entries(scores)
    .filter(([_, score]) => score > 0)
    .sort((a, b) => b[1] - a[1]);
  
  // Step 7: Determine primary category with elastic threshold
  let primaryCategory = null;
  let lowConfidence = false;
  
  if (sortedEntries.length > 0) {
    const topScore = sortedEntries[0][1];
    
    if (topScore >= 6) {
      // Strong match
      primaryCategory = sortedEntries[0][0];
    } else if (topScore >= 3) {
      // Lower confidence match
      primaryCategory = sortedEntries[0][0];
      lowConfidence = true;
    } else {
      // Very low score - use precedence list
      for (const categoryId of PRECEDENCE) {
        if (scores[categoryId] > 0) {
          primaryCategory = categoryId;
          lowConfidence = true;
          break;
        }
      }
    }
  }
  
  // Create sorted array of just category IDs for secondaries
  const sortedCategories = sortedEntries.map(([category]) => category);
  
  // Extract secondary categories (excluding the primary)
  const secondaryCategories = sortedCategories
    .filter(category => category !== primaryCategory)
    .slice(0, 2); // Limit to top 2 secondary categories
  
  const result = {
    primaryCategory,
    secondaryCategories,
    lowConfidence
  };
  categorizeCache.set(cacheKey, result);
  return result;
};

// Regex escape helper
function escRegex(str) {
  return str.replace(/[-/\\^$*+?.()|[\]{}]/g, "\\$&");
}

const initialKeywordScoring = (description) => {
  const scores = {};

  // Initialise all scores
  projectCategories.forEach(({ id }) => {
    scores[id] = 0;
  });

  // Clean description and pad with spaces for \b friendliness
  const normalizedText = ` ${description.replace(/[.,/#!$%^&*;:{}=_\-`~()]/g, ' ')} `;

  // 1) Exact matches – positives & negatives
  Object.entries(categoryWeights).forEach(([categoryId, config]) => {
    const { positive_keywords = {}, negative_keywords = {} } = config;

    // Positive hits add weight
    Object.entries(positive_keywords).forEach(([keyword, weight]) => {
      const re = new RegExp(`\\b${escRegex(keyword)}\\b`, 'i');
      if (re.test(normalizedText)) {
        scores[categoryId] += weight;
      }
    });

    // Negative hits subtract weight (penalties)
    Object.entries(negative_keywords).forEach(([keyword, penalty]) => {
      const re = new RegExp(`\\b${escRegex(keyword)}\\b`, 'i');
      if (re.test(normalizedText)) {
        scores[categoryId] -= penalty;
      }
    });
  });

  // 2) Fuzzy positive matching for strong keywords (weight >= 7)
  Object.entries(categoryWeights).forEach(([categoryId, config]) => {
    const { positive_keywords = {} } = config;

    Object.entries(positive_keywords).forEach(([keyword, weight]) => {
      if (weight < 7) return; // Only heavy hitters

      // Split description into candidate words > 3 chars
      const tokens = normalizedText.split(/\s+/).filter(w => w.length > 3);

      tokens.forEach(word => {
        if ((keyword.includes(' ') || keyword.length > 5) && !keyword.startsWith(word) && word.length > 4) {
          const dist = levenshteinDistance(word, keyword);
          const maxDist = Math.floor(keyword.length / 5) + 1;
          if (dist <= maxDist) {
            scores[categoryId] += Math.floor(weight / 2);
          }
        }
      });
    });
  });

  return scores;
};

/**
 * Apply special case rules that require more complex logic than
 * the standard boost/penalty system
 * 
 * @param {string} description - The cleaned application description
 * @param {Object} scores - Category scores to modify
 */
const applySpecialCaseRules = (description, scores) => {
  // Force category changes for small construction projects
  // Example: "Construct new internal wall" should be interior work, not new build
  if (/\b(construct|build|erect|form)\s+(internal|partition|wall|door|window|bathroom|kitchen|study)\b/i.test(description)) {
    // If new_build score exists and is significant
    if (scores['new_build_major'] > 3) {
      // Transfer points from new_build to interiors
      const transferPoints = Math.min(scores['new_build_major'], 5);
      scores['interiors_accessibility'] = (scores['interiors_accessibility'] || 0) + transferPoints;
      scores['new_build_major'] = Math.max(0, scores['new_build_major'] - transferPoints);
    }
  }
  
  // Check for small construction projects being miscategorized as major development
  if (/\bsmall\b|\bminor\b|\bsingle\b|\binternal\b/i.test(description) && 
      !/\blarge\b|\bmajor\b|\bblock\b|\bestate\b|\bdevelopment\b/i.test(description)) {
    if (scores['new_build_major'] > 0 && scores['new_build_major'] < 8) {
      // Reduce the new_build score for clearly minor projects
      scores['new_build_major'] = Math.max(0, scores['new_build_major'] - 3);
    }
  }
  
  // For change of use applications, ensure they get properly categorized
  if (/\bchange of use\b|\bchange the use\b|\bused as\b|\bfrom .* to .*\b/i.test(description)) {
    scores['change_of_use_conversions'] = Math.max(scores['change_of_use_conversions'], 8);
  }
  
  // For descriptions mentioning "space" or "void", make sure they don't get
  // miscategorized as shop spaces or signage
  if (/\bvoid\b|\bspace\b/i.test(description) && !/\bshop\b|\bretail\b|\bsign\b/i.test(description)) {
    scores['shopfront_signage'] = Math.max(0, (scores['shopfront_signage'] || 0) - 5);
  }
  
  // Special handling for manifestation and other glazing terms
  if (/\bmanifestation\b/i.test(description)) {
    // Manifestation is a glazing treatment, not a sign
    scores['windows_doors_glazing'] = (scores['windows_doors_glazing'] || 0) + 5;
    scores['shopfront_signage'] = Math.max(0, (scores['shopfront_signage'] || 0) - 2);
  }
  
  // Special case for garage conversion
  if (/\bgarage\s*conversion\b/i.test(description)) {
    scores['change_of_use_conversions'] = (scores['change_of_use_conversions'] || 0) + 4;
    scores['outbuildings_ancillary'] = (scores['outbuildings_ancillary'] || 0) + 3;
  }
};

/**
 * Helper function to calculate Levenshtein distance between two strings
 * This helps identify similar words with potential misspellings
 * 
 * @param {string} a - First string
 * @param {string} b - Second string
 * @returns {number} Edit distance between the strings
 */
function levenshteinDistance(a, b) {
  const matrix = Array(b.length + 1).fill().map(() => Array(a.length + 1).fill(0));
  
  for (let i = 0; i <= a.length; i++) {
    matrix[0][i] = i;
  }
  
  for (let j = 0; j <= b.length; j++) {
    matrix[j][0] = j;
  }
  
  for (let j = 1; j <= b.length; j++) {
    for (let i = 1; i <= a.length; i++) {
      const substitutionCost = a[i - 1] === b[j - 1] ? 0 : 1;
      
      matrix[j][i] = Math.min(
        matrix[j][i - 1] + 1, // deletion
        matrix[j - 1][i] + 1, // insertion
        matrix[j - 1][i - 1] + substitutionCost // substitution
      );
    }
  }
  
  return matrix[b.length][a.length];
}

/**
 * Get a category by its ID
 * @param {string} categoryId - The ID of the category to retrieve
 * @returns {Object|null} The category object or null if not found
 */
export const getCategoryById = (categoryId) => {
  if (!categoryId) return null;
  // If it's a low confidence category, remove the suffix before lookup
  if (categoryId.endsWith && categoryId.endsWith('__low')) {
    categoryId = categoryId.replace('__low', '');
  }
  // First try exact ID match
  const byId = projectCategories.find(category => category.id === categoryId);
  if (byId) return byId;
  // Then try label match (case-insensitive)
  const lower = categoryId.toLowerCase();
  return projectCategories.find(category => category.label.toLowerCase() === lower) || null;
};

/**
 * Get a list of applications by primary category ID
 * @param {Array} applications - The array of applications to filter
 * @param {string} categoryId - The category ID to filter by
 * @param {boolean} includeSecondary - Whether to include applications where the category is a secondary match
 * @returns {Array} Filtered applications that match the category
 */
export const getApplicationsByCategory = (applications, categoryId, includeSecondary = false) => {
  if (!applications || !categoryId) return [];
  
  // Strip __low suffix if present for matching
  const matchCategoryId = categoryId.replace('__low', '');
  
  return applications.filter(app => {
    // Get categorization for this application
    const result = categorizeApplication(app.description, app.reference_no);
    const { primaryCategory, secondaryCategories } = result;
    
    // Clean the categories (remove __low suffix for comparison)
    const cleanPrimaryCategory = primaryCategory ? primaryCategory.replace('__low', '') : null;
    const cleanSecondaryCategories = secondaryCategories.map(cat => cat.replace('__low', ''));
    
    // Check if primary category matches
    if (cleanPrimaryCategory === matchCategoryId) {
      return true;
    }
    
    // Check if any secondary category matches (if includeSecondary is true)
    if (includeSecondary && cleanSecondaryCategories.includes(matchCategoryId)) {
      return true;
    }
    
    return false;
  });
};

/**
 * Get the distribution of applications across primary categories
 * @param {Array} applications - The array of applications
 * @returns {Object} An object with category IDs as keys and count as values
 */
export const getCategoryDistribution = (applications) => {
  if (!applications || !applications.length) return {};
  
  const distribution = {};
  
  // Initialize all categories with 0
  projectCategories.forEach(category => {
    distribution[category.id] = 0;
  });
  
  // Count applications in each primary category
  applications.forEach(app => {
    const { primaryCategory } = categorizeApplication(app.description, app.reference_no);
    if (primaryCategory) {
      // Strip __low suffix if present
      const categoryId = primaryCategory.replace('__low', '');
      distribution[categoryId]++;
    }
  });
  
  return distribution;
};

// Simple in-memory cache scoped to the module
const categorizeCache = new Map();
